import type { ComposerCapabilityCategory, ComposerCapabilityExample } from '@/types/composer';
import {
  enrichComposerMenuCategories,
  enrichComposerMenuCategory,
} from '@/lib/resolve-composer-menu';

export interface CapabilityMenuStreamState {
  menuRevision: string | null;
  categories: ComposerCapabilityCategory[];
}

export interface CapabilityMenuStreamHandlers {
  getState: () => CapabilityMenuStreamState;
  setState: (next: CapabilityMenuStreamState) => void;
}

const CAPABILITY_EVENT_TYPES = new Set([
  'capability_menu_stream_start',
  'capability_category_start',
  'capability_example_delta',
  'capability_example_ready',
  'capability_category_stop',
  'capability_menu_stream_stop',
]);

export function isCapabilityMenuStreamEvent(eventType: string): boolean {
  return CAPABILITY_EVENT_TYPES.has(eventType);
}

function emptyExample(): ComposerCapabilityExample {
  return { label: '', message: '', placeholder: '' };
}

function revisionMatches(state: CapabilityMenuStreamState, revision: unknown): boolean {
  if (!revision || !state.menuRevision) return true;
  return String(revision) === state.menuRevision;
}

export function capabilityExampleIsReady(example: ComposerCapabilityExample): boolean {
  return Boolean(example.label?.trim() && example.message?.trim());
}

export function capabilityCategoryHasReadyExamples(
  category: ComposerCapabilityCategory,
): boolean {
  return (category.examples || []).some(capabilityExampleIsReady);
}

/** Pill label shimmer for the full category prefetch window (until streaming clears). */
export function isCapabilityCategoryPillStreaming(
  category: ComposerCapabilityCategory,
): boolean {
  return Boolean(category.streaming);
}

export function applyCapabilityMenuShells(
  menuRevision: string | null | undefined,
  categories: ComposerCapabilityCategory[],
  handlers: CapabilityMenuStreamHandlers,
): void {
  handlers.setState({
    menuRevision: menuRevision ? String(menuRevision) : null,
    categories: enrichComposerMenuCategories(
      categories.map((cat) => ({
        ...cat,
        examples: [...(cat.examples || [])],
      })),
    ).map((cat) => ({
      ...cat,
      streaming:
        (cat.streaming ?? true) && !capabilityCategoryHasReadyExamples(cat),
    })),
  });
}

function updateCategory(
  state: CapabilityMenuStreamState,
  categoryId: string,
  updater: (cat: ComposerCapabilityCategory) => ComposerCapabilityCategory,
): CapabilityMenuStreamState {
  return {
    ...state,
    categories: state.categories.map((cat) =>
      cat.id === categoryId ? updater(cat) : cat,
    ),
  };
}

function upsertExampleAtIndex(
  examples: ComposerCapabilityExample[],
  index: number,
): ComposerCapabilityExample[] {
  const next = [...examples];
  while (next.length <= index) {
    next.push(emptyExample());
  }
  return next;
}

/** Apply one capability menu SSE event. Returns true if handled. */
export function applyCapabilityMenuStreamEvent(
  eventType: string,
  data: Record<string, unknown>,
  handlers: CapabilityMenuStreamHandlers,
): boolean {
  const state = handlers.getState();

  switch (eventType) {
    case 'capability_menu_stream_start':
      if (data.menu_revision) {
        handlers.setState({
          menuRevision: String(data.menu_revision),
          categories: state.categories,
        });
      }
      return true;

    case 'capability_category_start': {
      if (!revisionMatches(state, data.menu_revision)) return true;
      const categoryId = String(data.category_id || '');
      if (!categoryId) return true;
      const exists = state.categories.some((c) => c.id === categoryId);
      const nextCategories = exists
        ? state.categories.map((cat) =>
            cat.id === categoryId ? { ...cat, streaming: true } : cat,
          )
        : [
            ...state.categories,
            enrichComposerMenuCategory({
              id: categoryId,
              title: categoryId,
              icon: 'Bot',
              tools: [],
              examples: [],
              streaming: true,
            }),
          ];
      handlers.setState({ ...state, categories: nextCategories });
      return true;
    }

    case 'capability_example_delta': {
      if (!revisionMatches(state, data.menu_revision)) return true;
      const categoryId = String(data.category_id || '');
      const exampleIndex = data.example_index;
      const field = data.field;
      const text = data.text;
      if (
        !categoryId ||
        typeof exampleIndex !== 'number' ||
        (field !== 'label' && field !== 'message' && field !== 'placeholder') ||
        typeof text !== 'string'
      ) {
        return true;
      }
      handlers.setState(
        updateCategory(state, categoryId, (cat) => {
          const examples = upsertExampleAtIndex(cat.examples, exampleIndex);
          const current = { ...examples[exampleIndex] };
          current[field] = (current[field] || '') + text;
          if (field === 'message' && !current.placeholder) {
            current.placeholder = current.message;
          }
          examples[exampleIndex] = current;
          return { ...cat, examples, streaming: true };
        }),
      );
      return true;
    }

    case 'capability_example_ready': {
      if (!revisionMatches(state, data.menu_revision)) return true;
      const categoryId = String(data.category_id || '');
      const exampleIndex = data.example_index;
      if (!categoryId || typeof exampleIndex !== 'number') return true;
      handlers.setState(
        updateCategory(state, categoryId, (cat) => {
          const examples = upsertExampleAtIndex(cat.examples, exampleIndex);
          examples[exampleIndex] = {
            label: String(data.label || ''),
            message: String(data.message || ''),
            placeholder: String(data.placeholder || data.message || ''),
          };
          return { ...cat, examples, streaming: true };
        }),
      );
      return true;
    }

    case 'capability_category_stop': {
      if (!revisionMatches(state, data.menu_revision)) return true;
      const categoryId = String(data.category_id || '');
      const examples = (data.examples || []) as ComposerCapabilityExample[];
      if (!categoryId) return true;
      handlers.setState(
        updateCategory(state, categoryId, (cat) => ({
          ...cat,
          examples: Array.isArray(examples) && examples.length ? examples : cat.examples,
          streaming: false,
        })),
      );
      return true;
    }

    case 'capability_menu_stream_stop':
      if (!revisionMatches(state, data.menu_revision)) return true;
      handlers.setState({
        ...state,
        categories: state.categories.map((cat) => ({ ...cat, streaming: false })),
      });
      return true;

    default:
      return false;
  }
}
